/**
 * Browser Web Notification API utilities.
 * Used for showing desktop notifications when the app tab is not focused, and for deciding when to
 * offer notifications in the app.
 */

import type { KeyValueStore } from '@elara/client-core/lib/key-value-store';

import { createWebKeyValueStore } from '#lib/web-key-value-store.js';

/** When the guest was last asked about notifications, and how it ended. */
const LAST_ASK_KEY = 'elara_notification_last_ask';
/** The boolean "already prompted" flag this replaced; removed on the next ask. */
const LEGACY_PROMPTED_KEY = 'elara_notification_permission_prompted';

/** How long after an ask before the app offers notifications again. */
export const NOTIFICATION_REASK_MS = 7 * 24 * 60 * 60 * 1000;

/** How an ask ended: the browser's answer, or the guest closing the offer before reaching it. */
export type NotificationAskOutcome = NotificationPermission | 'dismissed' | 'open_settings';

export interface NotificationAsk {
  /** Epoch milliseconds. */
  at: number;
  outcome: NotificationAskOutcome;
}

/** Whether the browser supports the Notification API */
export function isNotificationSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

/** Whether we already have notification permission */
export function hasNotificationPermission(): boolean {
  return isNotificationSupported() && Notification.permission === 'granted';
}

/**
 * Whether this is iOS/iPadOS Safari (or another iOS browser) in a regular tab. Web push there only
 * works from a Home Screen web app, so offering it in a tab would lead nowhere.
 */
export function isIosBrowserTab(): boolean {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
  const isIos =
    /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (!isIos) return false;
  const isStandalone =
    (navigator as Navigator & { standalone?: boolean }).standalone === true ||
    window.matchMedia?.('(display-mode: standalone)').matches === true;
  return !isStandalone;
}

/** Reads the last ask, or `undefined` when there is none (or it is unreadable). */
export function readLastNotificationAsk(store: KeyValueStore = createWebKeyValueStore()): NotificationAsk | undefined {
  const raw = store.get(LAST_ASK_KEY);
  if (!raw) return;
  try {
    const parsed = JSON.parse(raw) as Partial<NotificationAsk>;
    if (typeof parsed.at !== 'number' || typeof parsed.outcome !== 'string') return;
    return { at: parsed.at, outcome: parsed.outcome };
  } catch {
    return;
  }
}

/** Records that the guest was just asked, and how it ended. */
export function recordNotificationAsk(
  outcome: NotificationAskOutcome,
  store: KeyValueStore = createWebKeyValueStore(),
  now: number = Date.now(),
): void {
  store.set(LAST_ASK_KEY, JSON.stringify({ at: now, outcome } satisfies NotificationAsk));
  store.remove(LEGACY_PROMPTED_KEY);
}

/** What the app can offer about notifications right now. */
export type NotificationOffer = 'ask' | 'blocked';

/**
 * Whether to offer notifications, and which offer: `ask` while the browser can still prompt, `blocked`
 * (a pointer to settings) once the guest has blocked them. Nothing when they're on, unsupported, or
 * the guest was asked within {@link NOTIFICATION_REASK_MS}.
 *
 * @example
 * getNotificationOffer({ supported: true, iosBrowserTab: false, permission: 'default', lastAsk: undefined, now });
 * // 'ask'
 */
export function getNotificationOffer({
  supported,
  iosBrowserTab,
  permission,
  lastAsk,
  now,
}: {
  supported: boolean;
  iosBrowserTab: boolean;
  permission: NotificationPermission | undefined;
  lastAsk: NotificationAsk | undefined;
  now: number;
}): NotificationOffer | undefined {
  if (!supported || iosBrowserTab || !permission || permission === 'granted') return;
  if (lastAsk && now - lastAsk.at < NOTIFICATION_REASK_MS) return;
  return permission === 'denied' ? 'blocked' : 'ask';
}

/** {@link getNotificationOffer} for this browser, now. */
export function currentNotificationOffer(store: KeyValueStore = createWebKeyValueStore()): NotificationOffer | undefined {
  const supported = isNotificationSupported();
  return getNotificationOffer({
    supported,
    iosBrowserTab: isIosBrowserTab(),
    permission: supported ? Notification.permission : undefined,
    lastAsk: readLastNotificationAsk(store),
    now: Date.now(),
  });
}

/**
 * Request notification permission from the user, recording the ask so the app doesn't offer again
 * for {@link NOTIFICATION_REASK_MS}. Only prompts while the browser still can. Returns the permission.
 */
export async function requestNotificationPermission(): Promise<NotificationPermission | undefined> {
  if (!isNotificationSupported()) return;
  if (Notification.permission !== 'default') return Notification.permission;

  const result = await Notification.requestPermission();
  recordNotificationAsk(result);
  return result;
}

/**
 * Show a web notification when the document is not focused.
 * Clicking the notification focuses the app and navigates to the URL.
 */
export function showWebNotification(title: string, body?: string, url?: string): void {
  if (!hasNotificationPermission()) return;
  if (document.hasFocus()) return;

  const notification = new Notification(title, {
    body: body ?? undefined,
    icon: '/android-chrome-192x192.png',
  });

  if (url) {
    notification.onclick = () => {
      window.focus();
      window.location.href = url;
      notification.close();
    };
  }
}
